import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { TableContainer, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import {
   ColumnDef,
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { _js } from '@ifixit/localize';
import { color } from '@ifixit/primitives';
import { EditableCell, useTable, ActionButton, type TableRow } from './Table';

type WikiInfo = {
   name: string;
   value: string;
};

type WikiInfoColumn = {
   label: string;
   property: keyof WikiInfo;
   options?: string[];
};

type WikiInfoRow = {
   inheritedFrom: string | null;
   name: string;
   value: string;
};

type WikiInfoTableRow = TableRow<WikiInfoRow>;

const columnHelper = createColumnHelper<WikiInfoTableRow>();

function NewWikiInfoEdit({
   columns: wikiInfoColumns,
   rows: wikiInfoRows,
}: { columns: WikiInfoColumn[]; rows: WikiInfoRow[] }) {
   const { data, editCell, addRow, deleteRow } = useTable<WikiInfoRow>(wikiInfoRows);

   const columns: ColumnDef<WikiInfoTableRow, string>[] = useMemo(
      () => [
         columnHelper.accessor('name', {
            header: _js('Info'),
            cell: (props: any) => {
               const disableEdit = !!props.row.original.inheritedFrom;
               return (
                  <EditableCell
                     cellProps={props}
                     onSubmit={(newValue: string) => editCell(props.cell, newValue)}
                     isDisabled={disableEdit}
                     datalistOptions={wikiInfoColumns[0].options || []}
                  />
               );
            },
         }),
         columnHelper.accessor('value', {
            header: _js('Value'),
            cell: (props: any) => {
               const disableEdit = !!props.row.original.inheritedFrom;
               return (
                  <EditableCell
                     cellProps={props}
                     onSubmit={(newValue: string) => editCell(props.cell, newValue)}
                     isDisabled={disableEdit}
                  />
               );
            },
         }),
         columnHelper.display({
            id: 'actions',
            cell: ({ row }) => (
               <ActionButton
                  action={row.original.action}
                  rowIdx={row.index}
                  deleteRow={deleteRow}
                  addRow={addRow}
                  trashDisabled={!!row.original.inheritedFrom}
                  addDisabled={!row.original.name}
               />
            ),
         }),
      ],
      [editCell, addRow, deleteRow, wikiInfoColumns]
   );

   const table = useReactTable<WikiInfoTableRow>({
      data: data,
      columns,
      getCoreRowModel: getCoreRowModel(),
   });

   // Expose our state to non-React parts of the page. This isn't ideal,
   // but it swaps in well with our `wiki_edit.js` logic.
   // @ts-expect-error TS(2339) FIXME: Property 'WikiInfo' does not exist on type 'Window... Remove this comment to see the full error message
   window.WikiInfo = {
      getFormValues: () =>
         data
            .filter(
               row =>
                  // We don't want to send inherited rows to avoid duplication.
                  // inheritedFrom being null or undefined indicates that this
                  // row belongs directly to this wiki and wasn't inherited.
                  !(row.inheritedFrom || row.action === 'add')
            )
            .map(row => ({
               name: row.name,
               value: row.value,
            })),
   };

   return (
      <TableContainer borderRadius="md" border="1px" borderColor={color.gray[300]} bg="white">
         <Table>
            <Thead>
               {table.getHeaderGroups().map(headerGroup => (
                  <Tr key={headerGroup.id}>
                     {headerGroup.headers.map(header => (
                        <Th key={header.id}>
                           {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                        </Th>
                     ))}
                  </Tr>
               ))}
            </Thead>
            <Tbody>
               {table.getRowModel().rows.map(row => (
                  <Tr
                     key={row.id}
                     title={
                        row.original.inheritedFrom
                           ? `Inherited from: ${row.original.inheritedFrom}`
                           : ''
                     }
                  >
                     {row.getVisibleCells().map(cell => (
                        // @ts-expect-error TS(2322) FIXME: Type '"0px" | null' is not assignable to type 'Res... Remove this comment to see the full error message
                        <Td key={cell.id} w={cell.column.id === 'actions' ? '0px' : null}>
                           {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                     ))}
                  </Tr>
               ))}
            </Tbody>
         </Table>
      </TableContainer>
   );
}

initializeChakraComponent('WikiInfoEdit', NewWikiInfoEdit);
